import { FieldDef, GroupDef, GuiDef, ValidatorDef, TabDefinition, CollapsableDef } from '@/objects/gui';
import { StartingNumber } from '@/objects/generated';
import { markRaw, computed } from 'vue';
import {
    InputInt,
    ValidatedInt,
    InputText,
    ValidatedText,
    InputNullableDateTime,
    DateComp,
    InputBool,
    DisplayBool,
    InputNullableInt,
} from '@/components';
import { type DateType } from '@/utilities/dateutils';
import type { SelectLine, SelectLineValueType } from '@/objects/SelectLine';

export class StartingNumberGuiDef extends GuiDef<StartingNumber> {
    constructor() {
        super('StartingNumber');

        const Bib = new FieldDef<StartingNumber>();
        Bib.Name = 'Bib';
        Bib.FieldName = 'Bib';
        Bib.DefinedFor = 'Both';
        Bib.TranslationKey = 'startingnumber.bib';
        Bib.Editable = false;
        Bib.Filterable = false;
        Bib.Sortable = true;
        Bib.CssClasses = '';
        Bib.value = (startingnumber) => startingnumber.Bib;
        Bib.setValue = () => {};
        Bib.DetailEditor = markRaw(InputInt);
        Bib.DetailEditorProps = {};
        Bib.CellEditor = markRaw(InputInt);
        Bib.CellEditorProps = {};
        Bib.CellRenderer = markRaw(ValidatedInt);
        Bib.CellRendererProps = {};
        Bib.Validator = null;
        Bib.SortDirection = undefined;
        Bib.cellClassRules = undefined;
        Bib.MinWidth = undefined;
        Bib.MaxWidth = undefined;
        Bib.Tooltip = null;
        this.Fields.push(Bib);

        const Chip1 = new FieldDef<StartingNumber>();
        Chip1.Name = 'Chip1';
        Chip1.FieldName = 'Chip1';
        Chip1.DefinedFor = 'Both';
        Chip1.TranslationKey = 'startingnumber.chip1';
        Chip1.Editable = true;
        Chip1.Filterable = true;
        Chip1.Sortable = true;
        Chip1.CssClasses = '';
        Chip1.value = (startingnumber) => startingnumber.Chip1;
        Chip1.setValue = (startingnumber, value) => (startingnumber.Chip1 = value);
        Chip1.DetailEditor = markRaw(InputText);
        Chip1.DetailEditorProps = {};
        Chip1.CellEditor = markRaw(InputText);
        Chip1.CellEditorProps = {};
        Chip1.CellRenderer = markRaw(ValidatedText);
        Chip1.CellRendererProps = {};
        Chip1.Validator = null;
        Chip1.SortDirection = undefined;
        Chip1.cellClassRules = undefined;
        Chip1.MinWidth = undefined;
        Chip1.MaxWidth = undefined;
        Chip1.Tooltip = null;
        this.Fields.push(Chip1);

        const Chip2 = new FieldDef<StartingNumber>();
        Chip2.Name = 'Chip2';
        Chip2.FieldName = 'Chip2';
        Chip2.DefinedFor = 'Both';
        Chip2.TranslationKey = 'startingnumber.chip2';
        Chip2.Editable = true;
        Chip2.Filterable = true;
        Chip2.Sortable = true;
        Chip2.CssClasses = '';
        Chip2.value = (startingnumber) => startingnumber.Chip2;
        Chip2.setValue = (startingnumber, value) => (startingnumber.Chip2 = value);
        Chip2.DetailEditor = markRaw(InputText);
        Chip2.DetailEditorProps = {};
        Chip2.CellEditor = markRaw(InputText);
        Chip2.CellEditorProps = {};
        Chip2.CellRenderer = markRaw(ValidatedText);
        Chip2.CellRendererProps = {};
        Chip2.Validator = null;
        Chip2.SortDirection = undefined;
        Chip2.cellClassRules = undefined;
        Chip2.MinWidth = undefined;
        Chip2.MaxWidth = undefined;
        Chip2.Tooltip = null;
        this.Fields.push(Chip2);

        const StartTime = new FieldDef<StartingNumber>();
        StartTime.Name = 'StartTime';
        StartTime.FieldName = 'StartTime';
        StartTime.DefinedFor = 'Both';
        StartTime.TranslationKey = 'startingnumber.starttime';
        StartTime.Editable = true;
        StartTime.Filterable = true;
        StartTime.Sortable = true;
        StartTime.CssClasses = '';
        StartTime.value = (startingnumber) => startingnumber.StartTime;
        StartTime.setValue = (startingnumber, value) => (startingnumber.StartTime = value);
        StartTime.DetailEditor = markRaw(InputNullableDateTime);
        StartTime.DetailEditorProps = { format: 'DATETIME' as DateType };
        StartTime.CellEditor = markRaw(InputNullableDateTime);
        StartTime.CellEditorProps = { format: 'DATETIME' as DateType };
        StartTime.CellRenderer = markRaw(DateComp);
        StartTime.CellRendererProps = { format: 'DATETIME' as DateType };
        StartTime.Validator = null;
        StartTime.SortDirection = undefined;
        StartTime.cellClassRules = undefined;
        StartTime.MinWidth = 180;
        StartTime.MaxWidth = undefined;
        StartTime.Tooltip = null;
        this.Fields.push(StartTime);

        const IsBlocked = new FieldDef<StartingNumber>();
        IsBlocked.Name = 'IsBlocked';
        IsBlocked.FieldName = 'IsBlocked';
        IsBlocked.DefinedFor = 'Both';
        IsBlocked.TranslationKey = 'startingnumber.isblocked';
        IsBlocked.Editable = true;
        IsBlocked.Filterable = false;
        IsBlocked.Sortable = true;
        IsBlocked.CssClasses = '';
        IsBlocked.value = (startingnumber) => startingnumber.IsBlocked;
        IsBlocked.setValue = (startingnumber, value) => (startingnumber.IsBlocked = value);
        IsBlocked.DetailEditor = markRaw(InputBool);
        IsBlocked.DetailEditorProps = { label: 'startingnumber.isblocked' };
        IsBlocked.CellEditor = markRaw(InputBool);
        IsBlocked.CellEditorProps = {};
        IsBlocked.CellRenderer = markRaw(DisplayBool);
        IsBlocked.CellRendererProps = {};
        IsBlocked.Validator = null;
        IsBlocked.SortDirection = undefined;
        IsBlocked.cellClassRules = undefined;
        IsBlocked.MinWidth = undefined;
        IsBlocked.MaxWidth = undefined;
        IsBlocked.Tooltip = null;
        this.Fields.push(IsBlocked);

        const Target = new FieldDef<StartingNumber>();
        Target.Name = 'Target';
        Target.FieldName = 'Target';
        Target.DefinedFor = 'Both';
        Target.TranslationKey = 'startingnumber.target';
        Target.Editable = true;
        Target.Filterable = false;
        Target.Sortable = true;
        Target.CssClasses = '';
        Target.value = (startingnumber) => startingnumber.Target;
        Target.setValue = (startingnumber, value) => {
            if (value === undefined || value === '' || isNaN(value)) {
                value = null;
            }
            startingnumber.Target = value;
        };
        Target.DetailEditor = markRaw(InputNullableInt);
        Target.DetailEditorProps = {};
        Target.CellEditor = markRaw(InputNullableInt);
        Target.CellEditorProps = {};
        Target.CellRenderer = markRaw(ValidatedInt);
        Target.CellRendererProps = {};
        Target.Validator = null;
        Target.SortDirection = undefined;
        Target.cellClassRules = undefined;
        Target.MinWidth = undefined;
        Target.MaxWidth = undefined;
        Target.Tooltip = null;
        this.Fields.push(Target);

        const Bay = new FieldDef<StartingNumber>();
        Bay.Name = 'Bay';
        Bay.FieldName = 'Bay';
        Bay.DefinedFor = 'Both';
        Bay.TranslationKey = 'startingnumber.bay';
        Bay.Editable = true;
        Bay.Filterable = false;
        Bay.Sortable = true;
        Bay.CssClasses = '';
        Bay.value = (startingnumber) => startingnumber.Bay;
        Bay.setValue = (startingnumber, value) => {
            if (value === undefined || value === '' || isNaN(value)) {
                value = null;
            }
            startingnumber.Bay = value;
        };
        Bay.DetailEditor = markRaw(InputNullableInt);
        Bay.DetailEditorProps = {};
        Bay.CellEditor = markRaw(InputNullableInt);
        Bay.CellEditorProps = {};
        Bay.CellRenderer = markRaw(ValidatedInt);
        Bay.CellRendererProps = {};
        Bay.Validator = null;
        Bay.SortDirection = undefined;
        Bay.cellClassRules = undefined;
        Bay.MinWidth = undefined;
        Bay.MaxWidth = undefined;
        Bay.Tooltip = null;
        this.Fields.push(Bay);
    }

    public async LoadDependencies() {}
}
